import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEO from '@/components/feature/SEO';
import ComicCard from '@/components/feature/ComicCard';
import { comics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-bold text-foreground-950 mb-2">
              漫画未找到
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              您要查看的漫画可能已下架或不存在
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  const relatedComics = comics
    .filter(
      (c) =>
        c.id !== comic.id &&
        (c.categorySlug === comic.categorySlug ||
          c.tags.some((t) => comic.tags.includes(t)))
    )
    .slice(0, 6);

  const displayViews = (comic.views / 10000).toFixed(1);

  const articleSEO = {
    title: `${comic.title} - 女神漫画下拉式在线阅读 | 女神漫画网`,
    description: `${comic.title} - 女神漫画下拉式免费全集阅读。${comic.description.slice(0, 120)}`,
    keywords: `${comic.title},女神漫画下拉式,女神漫画下拉式${comic.category},女神漫画下拉式在线阅读,女神漫画下拉式免费全集`,
    canonicalUrl: `https://nvshenmh.com/article/${comic.id}`,
    ogType: 'article',
    ogImage: comic.coverImage,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${comic.title} - 女神漫画下拉式在线阅读`,
      description: comic.description,
      image: comic.coverImage,
      author: {
        '@type': 'Person',
        name: comic.author,
      },
      publisher: {
        '@type': 'Organization',
        name: '女神漫画网',
      },
      datePublished: '2026-01-01',
      dateModified: comic.updateDate,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://nvshenmh.com/article/${comic.id}`,
      },
    },
  };

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://nvshenmh.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: comic.category,
        item: `https://nvshenmh.com/category/${comic.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: comic.title,
        item: `https://nvshenmh.com/article/${comic.id}`,
      },
    ],
  };

  return (
    <>
      <SEO data={articleSEO} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }}
      />

      <Navbar />
      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
          <nav
            className="flex flex-wrap items-center gap-2 text-sm text-foreground-500"
            aria-label="面包屑导航"
          >
            <Link to="/" className="hover:text-primary-500 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link
              to={`/category/${comic.categorySlug}`}
              className="hover:text-primary-500 transition-colors"
            >
              {comic.category}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-950 font-medium">
              {comic.title}
            </span>
          </nav>
        </div>

        {/* Detail Section */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            {/* Cover */}
            <div className="flex-shrink-0 mx-auto md:mx-0">
              <div className="w-48 md:w-64 rounded-lg overflow-hidden bg-background-100">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - 女神漫画下拉式在线阅读`}
                  title={`${comic.title} - 女神漫画下拉式免费全集`}
                  className="w-full object-cover"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                {comic.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-foreground-600">
                  作者：{comic.author}
                </span>
                <span className="text-sm text-foreground-600">
                  类型：{comic.category}
                </span>
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}
                >
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-amber-500"></i>
                  {comic.rating} 分
                </span>
                <span>{displayViews} 万阅读</span>
                <span>共 {comic.totalChapters} 话</span>
                <span>更新于 {comic.updateDate}</span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {comic.description}
              </p>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button className="px-8 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer">
                  开始阅读
                </button>
                <button className="px-8 py-3 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer">
                  <i className="ri-bookmark-line mr-1.5"></i>加入收藏
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <h2 className="text-lg font-bold text-foreground-950 mb-6">
            章节列表
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {comic.chapters.map((ch) => (
              <button
                key={ch.id}
                className="flex items-center justify-between p-3 rounded-lg bg-background-50 hover:bg-background-100 border border-background-200 transition-colors cursor-pointer text-left"
              >
                <span className="text-sm font-medium text-foreground-800 truncate">
                  {ch.title}
                </span>
                <span className="text-xs text-foreground-400 ml-2 flex-shrink-0">
                  {ch.updateDate}
                </span>
              </button>
            ))}
          </div>
          {comic.totalChapters > comic.chapters.length && (
            <div className="text-center mt-4">
              <button className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
                查看更多章节 ({comic.totalChapters - comic.chapters.length} 话)
              </button>
            </div>
          )}
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 pb-16">
            <h2 className="text-lg font-bold text-foreground-950 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {relatedComics.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}